import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.quotes.QuoteContainer.';
  public static readonly quoteTitleNumber = `${Id.prefix}quoteTitleNumber`;
  public static readonly quoteTitleDraft = `${Id.prefix}quoteTitleDraft`;
  public static readonly quoteSaved = `${Id.prefix}quoteSaved`;
  public static readonly itemPriceUpdated = `${Id.prefix}itemPriceUpdated`;
  public static readonly gettingUpdatesFromSupplier = `${Id.prefix}gettingUpdatesFromSupplier`;
  public static readonly updatingSupplierQuoteItem = `${Id.prefix}updatingSupplierQuoteItem`;
  public static readonly supplierProcessingComplete = `${Id.prefix}supplierProcessingComplete`;
  public static readonly preparingToUpdate = `${Id.prefix}preparingToUpdate`;

  // New keys for error and UI messages
  public static readonly invalidArgumentQuoteId = `${Id.prefix}invalidArgumentQuoteId`;
  public static readonly quoteIsNull = `${Id.prefix}quoteIsNull`;
  public static readonly callNeedsQuoteItemsFirst = `${Id.prefix}callNeedsQuoteItemsFirst`;
  public static readonly quotePriceIsNull = `${Id.prefix}quotePriceIsNull`;
  public static readonly updateQuoteStateMissingThisQuote = `${Id.prefix}updateQuoteStateMissingThisQuote`;
  public static readonly invalidQuoteItemId = `${Id.prefix}invalidQuoteItemId`;
  public static readonly quotePresentationMissing = `${Id.prefix}quotePresentationMissing`;
}

export class LangQuoteContainer {
  private constructor() {}

  public static quoteTitleNumber(params: { quoteNumber: string | number; title: string }): string {
    return interpolate(Id.quoteTitleNumber, params);
  }

  public static quoteTitleDraft(params: { title: string }): string {
    return interpolate(Id.quoteTitleDraft, params);
  }

  public static quoteSaved(params: { title: string }): string {
    return interpolate(Id.quoteSaved, params);
  }

  public static itemPriceUpdated(params: { title: string }): string {
    return interpolate(Id.itemPriceUpdated, params);
  }

  public static gettingUpdatesFromSupplier(): string {
    return interpolate(Id.gettingUpdatesFromSupplier);
  }

  public static updatingSupplierQuoteItem(): string {
    return interpolate(Id.updatingSupplierQuoteItem);
  }

  public static supplierProcessingComplete(): string {
    return interpolate(Id.supplierProcessingComplete);
  }

  public static preparingToUpdate(): string {
    return interpolate(Id.preparingToUpdate);
  }

  // New methods for error and UI messages
  public static invalidArgumentQuoteId(): string {
    return interpolate(Id.invalidArgumentQuoteId);
  }

  public static quoteIsNull(): string {
    return interpolate(Id.quoteIsNull);
  }

  public static callNeedsQuoteItemsFirst(): string {
    return interpolate(Id.callNeedsQuoteItemsFirst);
  }

  public static quotePriceIsNull(): string {
    return interpolate(Id.quotePriceIsNull);
  }

  public static updateQuoteStateMissingThisQuote(): string {
    return interpolate(Id.updateQuoteStateMissingThisQuote);
  }

  public static invalidQuoteItemId(): string {
    return interpolate(Id.invalidQuoteItemId);
  }

  public static quotePresentationMissing(): string {
    return interpolate(Id.quotePresentationMissing);
  }
}

export class QuoteContainerModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.quoteTitleNumber, '#{{quoteNumber}} - {{title}}', 'Title for quote with number');
    register.add(Id.quoteTitleDraft, 'Draft - {{title}}', 'Title for draft quote');
    register.add(Id.quoteSaved, 'Quote Saved "{{title}}"', 'Toast message for quote saved');
    register.add(Id.itemPriceUpdated, 'Item Price Updated "{{title}}"', 'Toast message for item price updated');
    register.add(
      Id.gettingUpdatesFromSupplier,
      "getting updates from {{glossary 'supplier'}}",
      'Message for getting updates from supplier'
    );
    register.add(
      Id.updatingSupplierQuoteItem,
      "Updating {{glossary 'supplier'}} {{glossary 'quote-item:plural'}}",
      'Message for updating supplier quote items'
    );
    register.add(
      Id.supplierProcessingComplete,
      "{{glossary 'supplier'}} processing complete.\n\n+ sending updates to server",
      'Message for supplier processing complete'
    );
    register.add(Id.preparingToUpdate, 'Preparing to update', 'Message shown when preparing to update');

    // New registrations for error and UI messages
    register.add(
      Id.invalidArgumentQuoteId,
      'Invalid argument: Quote ID must match quoteId',
      'Error: invalid argument quoteId'
    );
    register.add(Id.quoteIsNull, 'Quote is null', 'Error: quote is null');
    register.add(Id.callNeedsQuoteItemsFirst, 'Call needsQuoteItems first', 'Error: needsQuoteItems not called');
    register.add(Id.quotePriceIsNull, 'QuotePrice is null', 'Error: quotePrice is null');
    register.add(
      Id.updateQuoteStateMissingThisQuote,
      'Return from Update QuoteState does not include this quote',
      'Error: updateQuoteState missing this quote'
    );
    register.add(Id.invalidQuoteItemId, 'Invalid quote item id', 'Error: invalid quote item id');
    register.add(
      Id.quotePresentationMissing,
      'QuotePresentation missing, cannot sort items',
      'Error: quotePresentation missing'
    );
  }
}
